import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Alert, CardContent } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StripeCardForm from "../../components/stripeCardForm/StripeCardForm";
import axios from "axios";
import Loader from "../../components/loader/loader";
import { getErrorMessage } from "../../Utils/getErrorMessage";

interface AddCreditCardProps {
  businessData?: any;
  businessToken?: string | null;
  paymentDetails?: any;
  returnUrl?: string;
}

const AddCreditCard: React.FC<AddCreditCardProps> = ({
  businessData: businessDataProp,
  businessToken: businessTokenProp,
  paymentDetails: paymentDetailsProp,
  returnUrl: returnUrlProp
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [existingCustomerId, setExistingCustomerId] = useState<string | null>(
    null
  );
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);

  // Get business details from localStorage
  const [businessData, setBusinessData] = useState<any>(
    businessDataProp || null
  );
  const [businessToken, setBusinessToken] = useState<string | null>(
    businessTokenProp || null
  );
  const [paymentDetails, setPaymentDetails] = useState<any>(
    paymentDetailsProp || null
  );

  // Get return URL from location state
  const returnUrl =
    returnUrlProp || location.state?.returnUrl || "/account/paymentdetails";

  // Function to fetch existing customer ID
  const fetchExistingCustomerId = useCallback(async () => {
    if (
      !businessData?.BusinessId ||
      !businessToken ||
      !paymentDetails?.ExternalCustomerId
    )
      return;

    setIsLoadingCustomer(true);
    try {
      // If paymentDetails are provided via props, use them directly
      if (paymentDetails?.ExternalCustomerId) {
        setExistingCustomerId(paymentDetails.ExternalCustomerId);
      } else {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/billingplatform-business/billingdetails/${businessData.BusinessId}`,
          {
            headers: {
              Authorization: `Bearer ${businessToken}`,
              "Content-Type": "application/json"
            }
          }
        );
        const details = response.data;
        setPaymentDetails(details);
        if (details?.ExternalCustomerId) {
          setExistingCustomerId(details.ExternalCustomerId);
        }
      }
    } catch (error: any) {
      console.log("No existing payment details found or error:", error);
      // This is not necessarily an error - the business might not have payment details yet
    } finally {
      setIsLoadingCustomer(false);
    }
  }, [businessData?.BusinessId, businessToken, paymentDetails]);

  // Initialize business data from localStorage
  useEffect(() => {
    if (!businessDataProp || !businessTokenProp) {
      const currentBusiness = localStorage.getItem("currentBusiness");
      const token = localStorage.getItem("businessAccessToken");
      if (currentBusiness && !businessData) {
        setBusinessData(JSON.parse(currentBusiness));
      }
      if (!businessToken) {
        setBusinessToken(token);
      }
    }
  }, [businessDataProp, businessTokenProp, businessData, businessToken]);

  // Check if we have required business data and fetch existing customer ID
  useEffect(() => {
    // Fetch existing customer ID if we have the required data
    if (businessData?.BusinessId) fetchExistingCustomerId();
  }, [businessData?.BusinessId, fetchExistingCustomerId]);

  const handleCardSuccess = async (
    newCustomerId: string,
    tokenId: string,
    email?: string
  ) => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    // Validate required data
    if (!businessData?.BusinessId) {
      setErrorMessage("Business information not found. Please log in again.");
      setIsLoading(false);
      return;
    }

    if (!businessToken) {
      setErrorMessage("Authentication token not found. Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      // Call your API to save the billing details
      let apiData: any;

      if (newCustomerId) {
        // Existing customer - send customer ID and token
        apiData = {
          // XeroCustomerId: paymentDetails?.XeroCustomerId
          //   ? paymentDetails?.XeroCustomerId
          //   : "",
          // MerchantExternalReferenceId: paymentDetails?.Id,
          CustomerId: newCustomerId,
          TokenId: tokenId
        };
      } else {
        // New customer - send email and token only
        apiData = {
          // XeroCustomerId: paymentDetails?.XeroCustomerId
          //   ? paymentDetails?.XeroCustomerId
          //   : "",
          // MerchantExternalReferenceId: paymentDetails?.Id,
          Email: email,
          TokenId: tokenId
        };
      }

      const config = {
        method: "POST",
        url: `${process.env.REACT_APP_API_BASE_URL}/billingplatform-business/billingdetails/${businessData.BusinessId}/stripe`,
        headers: {
          Authorization: `Bearer ${businessToken}`,
          "Content-Type": "application/json"
        },
        data: apiData
      };

      console.log("Calling API with data:", apiData);
      console.log("API URL:", config.url);

      const response = await axios.request(config);
      console.log("API Response:", response.data);

      // Show success message and redirect after delay
      setIsSuccess(true);
      setSuccessMessage(
        `Credit card ${existingCustomerId ? "updated" : "added"} successfully!`
      );

      // Auto redirect after 3 seconds
      setTimeout(() => {
        navigate(returnUrl);
      }, 2000);
    } catch (error: any) {
      console.error("Error saving payment method:", error);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardError = (error: string) => {
    setErrorMessage(error);
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate(returnUrl);
  };

  const renderContent = () => {
    if (isSuccess) {
      return (
        <Box sx={{ mt: 1, textAlign: "center" }}>
          <CheckCircleIcon
            sx={{ fontSize: 64, color: "success.main", mb: 2 }}
          />
          <Typography variant="h5" gutterBottom>
            Card Added Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your credit card has been securely added to your account.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting you back to payment details in a few seconds...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Enter your credit card information securely. Your card details are
          encrypted and processed by Stripe.
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        {isLoadingCustomer && (
          <Box sx={{ mt: 1, textAlign: "center", py: 4 }}>
            <Loader />
            <Typography variant="body1" color="text.secondary">
              Loading existing payment details...
            </Typography>
          </Box>
        )}

        {!isLoadingCustomer && (
          <StripeCardForm
            onSuccess={handleCardSuccess}
            onError={handleCardError}
            onCancel={handleCancel}
            loading={isLoading || isLoadingCustomer}
            existingCustomerId={existingCustomerId || undefined}
            businessEmail={businessData?.Email}
          />
        )}
      </Box>
    );
  };

  return <CardContent sx={{ mt: 2 }}>{renderContent()}</CardContent>;
};

export default AddCreditCard;
